import React, {ChangeEvent, useEffect, useState} from "react";
import { useTranslation } from 'react-i18next';
import { useSetAtom } from "jotai";
import { toastAtom } from "../../jotai_state";
import Api from "../../services/Api";
import profileIcon from "url:shared-assets/icons/profile.svg"
import pictureCircleIcon from "url:shared-assets/icons/picture-circle.svg"
import {Modal, ModalHeader, ModalBody} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import Checkbox from "../../components/Checkbox";
import EditPicture from "../../components/EditPicture/EditPicture";
import TabMenu from "../../components/TabMenu/TabMenu";
import {getLanguage} from "../../lang/helpers";
import trashIcon from "url:shared-assets/icons/trash.svg";
import {setUserType} from "../../constants";

interface CoachLanguageInterface {
  language: string;
  intro: string;
  native: boolean;
  aboutMe: string;
  coachingAreas: string;
  industryExperience: string;
  clientIndustries: string;
  philosophy: string;
}

interface NameValueInterface {
  name: string;
  value: string;
}

interface ProfileInterface {
  firstName: string;
  lastName: string;
  email: string;
  picture?: string;
  languages: Array<CoachLanguageInterface>
  coachingAreas: Array<NameValueInterface>
  coachingIndustriesCoach: Array<string>
  coachingIndustriesClient: Array<string>
  hasClientUser: boolean
}

export default function CoachAccount() {

  const api = Api.getInstance();
  const translation = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [profile, setProfile] = useState<ProfileInterface>()
  const [selectedLanguage, setSelectedLanguage] = useState<string>("")
  const [profileNative, setProfileNative] = useState<boolean>(false)
  const [profileIntro, setProfileIntro] = useState<string>("")
  const [profileAboutMe, setProfileAboutMe] = useState<string>("")
  const [profileCoachingAreas, setProfileCoachingAreas] = useState<string>("")
  const [profileIndustryExperience, setProfileIndustryExperience] = useState<string>("")
  const [profileClientIndustries, setProfileClientIndustries] = useState<string>("")
  const [profilePhilosophy, setProfilePhilosophy] = useState<string>("")
  const [picture, setPicture] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const setGlobalToast = useSetAtom(toastAtom);
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language)
  const coachLanguages = [
    "cs",
    "da",
    "nl",
    "en",
    "fi",
    "fr",
    "de",
    "el",
    "hu",
    "it",
    "no",
    "pl",
    "pt",
    "es",
    "sv",
    "lt"
  ]

  useEffect(() => {
    api.get('coach/profile').then((result) => {
      setProfile(result.json.profile)
      if (result.json.profile.languages.length > 0) {
        const firstLang: CoachLanguageInterface = result.json.profile.languages[0]
        setInputFromLanguage(firstLang)
      }
      setLoaded(true)
    })
  }, [])

  const checkAllInput = (languages: CoachLanguageInterface[]) => {
    let _error = ""
    if (!profile?.picture) {
      _error += lang === 'sv' ? 'Du måste ladda upp en profilbild.\n' : 'You must upload a profile picture.\n'
    }
    for (let l of languages) {
      let empty = 0
      if (l.intro.length < 1 || l.aboutMe.length < 1 || l.philosophy.length < 1) {
        empty++
      }
      if (empty > 0) {
        _error += lang === 'sv' ? 'Du måste fylla i alla fält markede med * för ' :
          'You must fill out all fields marked with * in '
        _error += t(l.language) + '.\n'
      }
    }
    return _error
  }

  const save = () => {
    if (!profile || !profile.languages) return;
    // The pain! To ensure we get the latest changes, we need to look at both profile.languages and e.g profileIntro
    const languages: Array<CoachLanguageInterface> = []
    for (const language of profile.languages) {
      if (language.language === selectedLanguage) {
        languages.push({
          language: selectedLanguage,
          native: profileNative,
          intro: profileIntro,
          aboutMe: profileAboutMe,
          coachingAreas: profileCoachingAreas,
          industryExperience: profileIndustryExperience,
          clientIndustries: profileClientIndustries,
          philosophy: profilePhilosophy
        })
      } else {
        languages.push(language)
      }
    }

    const data = {
      "languages": languages
    }

    let _error = checkAllInput(languages)

    setError(_error)
    if (_error.length > 0) {
      return
    }

    api.post('coach/profile/save', data).then((result) => {
      setGlobalToast({
        text: lang === 'sv' ? 'Sparade dina inställningar' : 'Saved your settings',
      });
    })
  }

  const deleteLanguage = (language: string) => {

    if (!profile) return;
    api.get('coach/profile/language/delete/' + language).then((result) => {
      const newProfile = structuredClone(profile)
      const languages: Array<CoachLanguageInterface> = []
      for (const lang of profile.languages) {
        if (lang.language != language) {
          languages.push(lang)
        }
      }
      newProfile.languages = languages
      setProfile(newProfile)
      if (language === selectedLanguage) {
        if (languages.length > 0) {
          setSelectedLanguage(languages[0].language)
        } else {
          setSelectedLanguage("")
        }
      }
    })
  }

  const setInputFromLanguage = (coachLanguage: CoachLanguageInterface) => {
    setSelectedLanguage(coachLanguage.language)
    setProfileNative(coachLanguage.native)
    setProfileIntro(coachLanguage.intro)
    setProfileAboutMe(coachLanguage.aboutMe)
    setProfileCoachingAreas(coachLanguage.coachingAreas)
    setProfileIndustryExperience(coachLanguage.industryExperience)
    setProfileClientIndustries(coachLanguage.clientIndustries)
    setProfilePhilosophy(coachLanguage.philosophy)
  }

  useEffect(() => {
    // When changing language, check if we should add new, set to empty or use existing

    // Used when adding new language or setting language to "Choose language"
    const emptyLanguage: CoachLanguageInterface = {
      language: selectedLanguage,
      native: false,
      intro: '',
      aboutMe: '',
      coachingAreas: '',
      industryExperience: '',
      clientIndustries: '',
      philosophy: ''
    }

    if (selectedLanguage === "") {
      setInputFromLanguage(emptyLanguage)
    } else if (profile) {
      let foundLangTexts: CoachLanguageInterface|null = null
      for (let l of profile.languages) {
        if (l.language === selectedLanguage) {
          foundLangTexts = l
          break
        }
      }
      if (foundLangTexts) {
        setInputFromLanguage(foundLangTexts)
      } else {
        const newProfile = structuredClone(profile)
        setInputFromLanguage(emptyLanguage)
        newProfile.languages.push(emptyLanguage)
        setProfile(newProfile)
      }
    }

  }, [selectedLanguage])

  const updatePreviousLanguage = () => {
    if (!profile) return;
    // Take the current values for intro, expertise, etc, and insert into the current profile
    const newProfile = structuredClone(profile)
    for (let l of newProfile.languages) {
      if (l.language == selectedLanguage) {
        l.intro = profileIntro
        l.native = profileNative
        l.aboutMe = profileAboutMe
        l.coachingAreas = profileCoachingAreas
        l.industryExperience = profileIndustryExperience
        l.clientIndustries = profileClientIndustries
        l.philosophy = profilePhilosophy
        break
      }
    }
    setProfile(newProfile)
  }

  const selectLanguage = (language: string) => {
    // Check if we should insert previous data into profile structure
    updatePreviousLanguage()

    setSelectedLanguage(language)
  }

  const editLanguage = (e: ChangeEvent<HTMLTextAreaElement>, what: string) => {
    if (what === "intro") {
      if (e.target.value.length > 110) {
        alert("Too long! Max length of intro is 110 characters.")
      } else {
        setProfileIntro(e.target.value)
      }
    } else if (what === "aboutMe") {
      setProfileAboutMe(e.target.value)
    } else if (what === "coachingAreas") {
      setProfileCoachingAreas(e.target.value)
    } else if (what === "industryExperience") {
      setProfileIndustryExperience(e.target.value)
    } else if (what === "clientIndustries") {
      setProfileClientIndustries(e.target.value)
    } else if (what === "philosophy") {
      setProfilePhilosophy(e.target.value)
    }
  }

  if (!loaded) {
    return (<div className="mt-4">Loading profile...</div>)
  }

  const closeEditPicture = (picture: string)=> {
    if (profile && picture && picture.length > 0) {
      setProfile({...profile, picture})
    }
    setPicture(false)
  }

  const renderTab = () => {
    if (!profile || profile.languages.length < 2) {
      return (<></>)
    }

    const items = []
    let active = 0
    let i = 0
    for (const l of profile.languages) {
      if (l.language === selectedLanguage) {
        active = i
      }
      items.push({
        title: t(l.language),
        callback: () => selectLanguage(l.language)
      })
      i++
    }

    return (
      <div className="mt-4">
        <TabMenu items={items} active={active} />
      </div>
    )

  }

  return (
    <div className="main-content">
      {
        picture &&
        <EditPicture picture={profile?.picture} close={closeEditPicture} lang={lang} who={"coach"}/>
      }
      <Modal isOpen={showConfirmDelete} onClose={() => setShowConfirmDelete(false)}>
        <ModalHeader onClose={() => setShowConfirmDelete(false)}>
          {lang === 'sv' ? 'Ta bort presentation' : 'Remove presentation'}
        </ModalHeader>
        <ModalBody>
          <div className="p-4">
            {lang === 'sv' ? 'Är du säker på att du vill ta bort presentation på ' :
              'Are you sure you want to remove the presentation in '}
            {t(selectedLanguage)}?
            <CustomButton className="w-full mt-8"
              onClick={() => {setShowConfirmDelete(false); deleteLanguage(selectedLanguage)}}>
              {t('yes')}
            </CustomButton>
          </div>
        </ModalBody>
      </Modal>
      <h1 className="text-2xl font-medium text-white mt-4">
        {t('settings')}
      </h1>

      <div className="flex mt-6 items-center">
        <div className="relative cursor-pointer bg-white rounded-full" onClick={() => setPicture(true)}>
          <img className={"picture-lg " + ((profile && profile.picture) ? '' : 'p-3')} alt="pic"
               src={(profile && profile.picture ? profile.picture : profileIcon)}/>
          <img alt="pic-icon" style={{position: "absolute", zIndex: 10, bottom: "0px", right: "0px"}}
               src={pictureCircleIcon}/>
        </div>
        <div className="ml-4">
          <p className="text-xl font-medium">{profile?.firstName} {profile?.lastName}</p>
          <p className="text-neutral-400">{profile?.email}</p>
          {
            profile?.hasClientUser &&
            <div className="z-link" onClick={() => setUserType("client")}>
              {lang === "sv" ? "Byt till ditt klient-konto" : "Switch to your client account"}
            </div>
          }
        </div>
      </div>

      <div className="mt-6 text-xl font-medium">{t('language')}</div>

      <div className="mt-2">
        {lang === 'sv' ? 'Lägg till språk att coacha på': 'Add language to coach in'}
      </div>

      <select onChange={(e) => selectLanguage(e.target.value)}
              className="z-select mt-2" value="">
        <option value="">{t("select-language")}</option>
        {
          coachLanguages.map(l => {
            return (
              <option value={l} key={"lang_" + l}>{t(l)}</option>
            )
          })
        }
      </select>

      <div className="mt-6 text-xl font-medium">
        Public presentation
      </div>

      {renderTab()}

      {
        selectedLanguage != "" &&
        <div className="mt-4">
            <div className="flex items-center justify-between mt-4">
                <div className="flex items-center">
                    <div onClick={() => setProfileNative(!profileNative)}>
                        <Checkbox checked={profileNative} label={lang === 'sv' ? 'Modersmål' : 'Native language'}
                                  id="_native"/>
                    </div>
                </div>
                <img src={trashIcon} alt="delete" className="cursor-pointer"
                     onClick={() => setShowConfirmDelete(true)}/>
            </div>

            <div className="mt-4 font-medium">{lang === 'sv' ? 'Kort introduktion' : 'Short introduction'} *</div>
            <p className="text-xs text-neutral-400">
              {
                lang === 'sv' ?
                  'Summera din coach approach eller din expertis, t ex "Hjälper ledare växa, bygga uthållighet och nå bestående framgång."'
                  :
                  'Summarize your coaching approach or highlight your expertise, e.g. "Empowering leaders to grow, build resilience, and achieve lasting success."'
            }
          </p>
          <textarea onChange={(e) => editLanguage(e, 'intro')}
                    placeholder={t('write-text-here')}
                    value={profileIntro}
                    className="z-input mt-4 w-full h-40"/>
            <div className="flex justify-end text-xs text-neutral-400">{profileIntro.length}/110</div>

          <hr className="mt-4 border-neutral-700"/>

          <div className="mt-4 font-medium">{lang === 'sv' ? 'Om mig': 'About me'} *</div>
          <textarea onChange={(e) => editLanguage(e, 'aboutMe')}
                    placeholder={t('write-text-here')}
                    value={profileAboutMe}
                    className="z-input mt-4 h-40"/>

          <hr className="mt-4 border-neutral-700"/>

          <div className="mt-4 font-medium">{lang === 'sv' ? 'Coachområden': 'Coaching areas'}</div>
          <textarea onChange={(e) => editLanguage(e, 'coachingAreas')}
                    placeholder={t('write-text-here')}
                    value={profileCoachingAreas}
                    className="z-input mt-4 h-40"/>

          <div className="mt-4 flex flex-wrap">
            {
              profile?.coachingAreas.map(area => (
                <div key={"area_" + area.name} className="rounded-full px-6 py-2 bg-neutral-600 mr-2 mb-2">{area.name}</div>
              ))
            }
          </div>

          <hr className="mt-4 border-neutral-700"/>

          <div className="mt-4 font-medium">{lang === 'sv' ? 'Brancherfarenhet': 'Industry experience'}</div>
          <textarea onChange={(e) => editLanguage(e, 'industryExperience')}
                    placeholder={t('write-text-here')}
                    value={profileIndustryExperience}
                    className="z-input mt-4 h-40"/>

          <div className="mt-4 flex flex-wrap">
            {
              profile?.coachingIndustriesCoach.map(name => (
                <div key={"area_" + name} className="rounded-full px-6 py-2 bg-neutral-600 mr-2 mb-2">{name}</div>
              ))
            }
          </div>

          <hr className="mt-4 border-neutral-700"/>

          <div className="mt-4 font-medium">{lang === 'sv' ? 'Klienters branscher': 'Client industries'}</div>
          <textarea onChange={(e) => editLanguage(e, 'clientIndustries')}
                    placeholder={t('write-text-here')}
                    value={profileClientIndustries}
                    className="z-input mt-4 h-40"/>

          <div className="mt-4 flex flex-wrap">
            {
              profile?.coachingIndustriesClient.map(name => (
                <div key={"area_" + name} className="rounded-full px-6 py-2 bg-neutral-600 mr-2 mb-2">{name}</div>
              ))
            }
          </div>

          <hr className="mt-4 border-neutral-700"/>

          <div className="mt-4 font-medium">{t('profile-philosophy')} *</div>
          <textarea onChange={(e) => editLanguage(e, 'philosophy')}
                    placeholder="Write text here..."
                    value={profilePhilosophy}
                    className="z-input mt-4 h-40"/>

        </div>
      }

      {
        error.length > 0 &&
        <div className="mt-4 p-4 border rounded border-cherry-500 whitespace-pre-line">{error}</div>
      }

      {
        (profile && profile.languages.length > 0) &&
        <CustomButton
          onClick={() => save()}
          className="w-full my-4">
          {t('save')}
        </CustomButton>
      }

    </div>
  );
};